/**
 * Centralized mobile back-button interceptor.
 *
 * Any overlay (modal, chat sheet, drawer, etc.) can push a close callback.
 * Pressing the browser/device back button pops the top callback and calls it
 * instead of navigating away. The system maintains exactly ONE guard history
 * entry while the stack is non-empty.
 *
 * Usage:
 *   const remove = pushBackHandler(() => closeMyModal())
 *   // later, when the modal closes normally (not via back):
 *   remove()
 */

type BackHandler = () => void

const stack: { id: number; handler: BackHandler }[] = []
let nextId = 1
let guardPushed = false
let listening = false
let suppressNext = false

function onPopState() {
  if (suppressNext) {
    suppressNext = false
    return
  }

  if (stack.length === 0) return

  // The guard entry was just consumed by the back navigation
  guardPushed = false

  // Pop & call the top handler
  const top = stack.pop()!
  top.handler()

  // If there are still entries remaining, re-push the guard.
  // Defer to next microtask — some mobile browsers misbehave when
  // pushState is called synchronously inside a popstate handler.
  if (stack.length > 0) {
    setTimeout(() => {
      if (stack.length > 0 && !guardPushed) {
        pushGuard()
      }
    }, 0)
  }
}

function pushGuard() {
  const guardKey = '__backGuard'
  window.history.pushState(
    { ...(window.history.state || {}), [guardKey]: Date.now() },
    '',
    window.location.href,
  )
  guardPushed = true
}

function ensureListening() {
  if (listening) return
  listening = true
  window.addEventListener('popstate', onPopState)
}

/**
 * Push a back-button handler onto the stack.
 * Returns a cleanup function that removes this handler (call it when the
 * overlay closes through its own UI, NOT via back button).
 */
export function pushBackHandler(handler: BackHandler): () => void {
  if (typeof window === 'undefined') return () => {}

  ensureListening()

  const id = nextId++
  stack.push({ id, handler })

  // Only push a guard entry if we don't already have one
  if (!guardPushed) {
    pushGuard()
  }

  let removed = false
  return () => {
    if (removed) return
    removed = true

    const idx = stack.findIndex((e) => e.id === id)
    if (idx === -1) return
    stack.splice(idx, 1)

    // If stack is now empty, clean up the guard history entry
    if (stack.length === 0 && guardPushed) {
      guardPushed = false
      suppressNext = true
      window.history.back()
    }
  }
}
